<script>
export default {
	name: 'MHTable',
	inheritAttrs: false,
};
</script>

<script setup>
const props = defineProps({
	desktopColumnWidth: { type: String, default: null }, // Example: 300 | auto | 10%,
	mobileColumnWidth: { type: String, default: null }, // Example: 300 | auto | 10%,
	lockColumn: { type: String, default: 'none' }, // none | Number
	tableData: { type: String, default: '' },
	rowAltColor: { type: Boolean, default: false }, // hexcode | color name
	tableAlignment: { type: String, default: 'left' }, // left, right, center
});

const emit = defineEmits([
	// 'update:modelValue',
]);

const root = ref(null);
const noOfColumnsLocked = isNaN(props.lockColumn) ? 0 : parseInt(props.lockColumn);

function updateTableSizes () {
	if (!root.value) return;
	
	root.value.style.setProperty(
		'--table-w-desktop',
		props.desktopColumnWidth?.includes('%') ? '100%' : 'max-content'
	);
	root.value.style.setProperty(
		'--table-w-mobile',
		props.mobileColumnWidth?.includes('%') ? '100%' : 'max-content'
	);

	root.value.style.setProperty(
		'--root-display-desktop',
		props.desktopColumnWidth?.includes('%') ? 'block' : 'flex'
	);

	root.value.style.setProperty(
		'--root-display-mobile',
		props.mobileColumnWidth?.includes('%') ? 'block' : 'flex'
	);

	if (!props.rowAltColor) {
		root.value.style.setProperty('--alternate-bg-color', 'white');
	}
}

function updateColumnWidth () {
	const columnWidthsDesktop = props.desktopColumnWidth?.split(',') || [];
	const columnWidthsMobile = props.mobileColumnWidth?.split(',') || [];
	const tableWidth = root.value.clientWidth;
	
	function getPixelWidth (percentage) {
		if (!percentage) return '';
		if (String(percentage).includes('px')) return percentage;
		if (tableWidth === 0) {
			return 'auto';
		} else {
			return `${tableWidth * String(percentage).replace('%', '') / 100}px`;
		}
	}

	root.value.querySelectorAll('tr:first-of-type > *').forEach((el, index) => {
		el.style.setProperty(
			'--column-width-desktop',
			getPixelWidth(columnWidthsDesktop[index]) || 'auto'
		);
		el.style.setProperty(
			'--column-width-mobile',
			getPixelWidth(columnWidthsMobile[index]) || 'auto'
		);
	});
}

function lockColumns () {
	if (!noOfColumnsLocked) return;
	
	root.value
		.querySelectorAll(`table td:nth-child(-n + ${noOfColumnsLocked}), table th:nth-child(-n + ${noOfColumnsLocked})`)
		.forEach((item) => {
			item.style.left = `${item.offsetLeft}px`;
			item.classList.add('sticky-cell');
			if (item.matches(`:nth-child(${noOfColumnsLocked})`)) {
				item.classList.add('last-col');
			}
		});
}

function observeParents () {
	const PARENTS_THAT_COULD_BE_HIDDEN = ['.Accordion .content', '.SwitchTabContent [data-tab-id]'];
	const observeConfig = {
		attributes: true,
	};

	const observer = new MutationObserver((mutationList) => {
		let needToUpdate = false;

		for (const mutation of mutationList) {
			if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
				needToUpdate = needToUpdate || mutation.target.style.display !== 'none';

				// Adjusting Accordion height. Since table height may change when during updateColumnWidth()
				if (needToUpdate && mutation.target.matches('.Accordion .content.simple-slide-down-enter-active')) {
					setTimeout(() => {
						mutation.target.style.height = `${mutation.target.scrollHeight}px`;
					}, 10);
				}
			}
		}

		if (needToUpdate) {
			updateColumnWidth();
		}
	});

	PARENTS_THAT_COULD_BE_HIDDEN.forEach((selector) => {
		const parentEl = root.value.closest(selector);
		if (parentEl) {
			observer.observe(parentEl, observeConfig);
		}
	});

	onUnmounted(() => {
		observer?.disconnect?.();
	});
}

onMounted(() => {
	updateTableSizes();
	updateColumnWidth(); // updateColumnWidth() must call before lockColumns()
	observeParents();
	lockColumns();

	window.addEventListener('resize', updateColumnWidth, { passive: true });
});

onUnmounted(() => {
	window.removeEventListener('resize', updateColumnWidth, { passive: true });
});

const siteName = window.siteName;

</script>

<template>
<div
	ref="root"
	class="MHTable overflow-hidden rounded-md author-html-container"
	:class="{
		'justify-start': props.tableAlignment === 'left',
		'justify-center': props.tableAlignment === 'centre',
		'justify-end': props.tableAlignment === 'right',
	}"
	:data-use-theme="siteName"
>
	<div
		v-html-sanitize="props.tableData"
		class="table-wrapper styled-scrollbar column-locked"
		:class="{
			'table-center-desktop': props.desktopColumnWidth?.includes('%'),
			'table-center-mobile': props.mobileColumnWidth?.includes('%'),
		}"
	></div>
</div>
</template>

<style lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.MHTable {
	--root-display-desktop: block;
	--root-display-mobile: block;
	--root-display: var(--root-display-desktop);
	--table-w-desktop: auto;
	--table-w-mobile: auto;
	--table-width: var(--table-w-desktop);
	--cell-padding: 1rem; /* 16px */
	--header-bg: #{var.$primary-blue-dark};
	--header-color: #{var.$neutral-white-base};
	--rounded-width: 0.5rem;
	--border-color: #{var.$neutral-grey-light};
	--alternate-bg-color: #{var.$neutral-grey-extralight};
	position: relative;
	display: var(--root-display);

	@media #{var.$query-max-md} {
		--table-width: var(--table-w-mobile);
		--root-display: var(--root-display-mobile);
	}


	.table-wrapper {
		width: var(--table-width);
		position: relative;
		overflow-x: auto;
		border-top-left-radius: var(--rounded-width);
	}

	table {
		position: relative;
		// table-layout: fixed; <-- disabled this due to issue on safari, the table height 0 and the component not showing
		width: var(--table-width);
		overflow: scroll;
		/* overflow: scroll; */
		border-radius: var(--rounded-width);
		border-collapse: separate;
		border-spacing: 0;
	}
	.table-center-desktop table {
		@apply >md:w-auto >md:mx-auto;
	}
	.table-center-mobile table {
		@apply md:w-auto md:mx-auto;
	}

	thead, tbody {
		display: block;
	}

	td, th {
		@apply text-left;
		padding: var(--cell-padding);
	}

	th {
		@apply font-bold;
		background-color: var(--header-bg);
		color: var(--header-color)
	}

	tr:first-of-type > * {
		--column-width-mobile: auto;
		--column-width-desktop: auto;
		--column-width: var(--column-width-desktop);
		width: var(--column-width);
		
		@media #{var.$query-max-md} {
			--column-width: var(--column-width-mobile);
		}
	}

	tr:first-of-type > * {
		background-color: var(--header-bg);
		color: var(--header-color);
		font-weight: 700;
	}

	tr:nth-child(even) > td {
		background-color: var(--alternate-bg-color);
	}
	tr:nth-child(odd):not(:first-of-type) > td {
		@apply bg-neutral-white-base;
	}

	/*------------- */
	/* Table Header */
	/*------------- */
	tr:first-of-type > *:first-child {
		border-top-left-radius: var(--rounded-width);
		border-left: 1px solid var(--header-bg);
	}
	tr:first-of-type > *:last-child {
		border-top-right-radius: var(--rounded-width);
		border-right: 1px solid var(--header-bg);
	}

	/*------------ */
	/* Table Body  */
	/*------------ */
	tr:not(:first-of-type) > *:first-of-type {
		border-left: 1px solid var(--border-color);
	}
	tr:not(:first-of-type) > *:last-of-type {
		border-right: 1px solid var(--border-color);
	}
	tr:not(:first-of-type) > *:not(:last-of-type) {
		border-right: 1px solid var(--border-color);
	}
	tr:not(:last-of-type):not(:first-of-type) > * {
		border-bottom: 1px solid var(--border-color);
	}

	/*----------*/
	/* Table Header other than first row */
	/*----------*/

	tr:not(:first-of-type) th:first-of-type {
		border-left: none;
	}
	tr:not(:first-of-type) th:last-of-type {
		border-right: none;
	}
	tr:not(:first-of-type) th:not(:last-of-type) {
		border-right: none;
	}
	tr:not(:last-of-type):not(:first-of-type) th {
		border-bottom: none;
	}
	tr:last-of-type th:first-child {
		border-bottom-left-radius: var(--rounded-width);
	}
	tr:last-of-type > th {
		border-bottom: none;
	}

	/*----------*/
	/* Last Row */
	/*----------*/
	tr:last-of-type > *:first-child {
		border-bottom-left-radius: var(--rounded-width);
	}
	tr:last-of-type > *:last-child {
		border-bottom-right-radius: var(--rounded-width);
	}
	tr:last-of-type > * {
		border-bottom: 1px solid var(--border-color);
	}

	/*--------------*/
	/* Sticky Column */
	/*--------------*/
	.sticky-cell {
		z-index: 1;
		position: sticky;
		width: var(--column-width);
		left: 0;
		
		/* &:not(th) {
			@apply font-bold;
		} */
		
		&.last-col:after {
			content: '';
			position: absolute;
			right: 0px;
			width: 12px;
			box-shadow: 11px 0px 12px 0px color.change(var.$primary-black-base, $alpha: 0.12);
			@apply inset-y-0;
		}
	}
}

div[data-cq-data-path*="mh_table"] {
	--a-tag-color: var(--primary-blue-base);
	--a-tag-color-hover: var(--primary-blue-dark);
	
	a {
		word-break: break-all;
	}
	h1, h2, h3, h4, h5, h6 {
		margin: revert;
	}
	blockquote, dl, dd, hr, figure, p, pre {
		margin: revert;
	}
	ul, ol {
		list-style-type: revert;
		padding: revert;
		margin: revert;
	}
}

.MHTable[data-use-theme="firefly"] {
	--header-bg: var(--secondary-firefly-orange-base);
}
</style>
